import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
// Session is carried by an httpOnly cookie set by the backend; nothing is stored in localStorage.
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const PUBLIC_PATHS = ['/login', '/signup', '/pricing', '/privacy-policy'];

const readCookie = (name) => {
  const item = document.cookie.split(';').map((c) => c.trim()).find((c) => c.startsWith(`${name}=`));
  return item ? decodeURIComponent(item.slice(name.length + 1)) : null;
};

/** Headers required by the backend CSRF middleware on state-changing requests (for raw fetch calls). */
export const csrfHeaders = () => {
  const token = readCookie('hmc_csrf');
  return token ? { 'X-CSRF-Token': token } : {};
};

// Double-submit CSRF: mirror the readable hmc_csrf cookie into a header on every mutating request
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    Object.assign(config.headers, csrfHeaders());
  }
  return config;
});

// Response interceptor: bounce to login when the session is invalid/expired
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthProbe = error.config?.url?.endsWith('/auth/me');
    const onPublicPage = PUBLIC_PATHS.some(p => window.location.pathname.startsWith(p));
    if (error.response?.status === 401 && !isAuthProbe && !onPublicPage) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (data) => api.post('/auth/change-password', data),
  adminChangePassword: (userId, data) => api.post(`/auth/admin-change-password/${userId}`, data),
};

// Institution APIs (Multi-tenant)
export const institutionAPI = {
  // Public signup (no auth required)
  signup: (data) => axios.post(`${API_BASE}/public/institution-signup`, data),
  // Authenticated endpoints
  getAll: () => api.get('/institutions'),
  getById: (id) => api.get(`/institutions/${id}`),
  update: (id, data) => api.put(`/institutions/${id}`, data),
  subscribe: (id, data) => api.post(`/institutions/${id}/subscribe`, data),
  // Usage & Features
  getUsage: () => api.get('/institution/usage'),
  getFeatures: () => api.get('/institution/features'),
  checkFeature: (feature) => api.get(`/institution/check-feature/${feature}`),
};

// Platform Admin APIs
export const platformAPI = {
  getStats: () => api.get('/platform/stats'),
  getInstitutions: (params) => api.get('/platform/institutions', { params }),
  suspendInstitution: (id, data) => api.put(`/platform/institutions/${id}/suspend`, data),
  reactivateInstitution: (id) => api.put(`/platform/institutions/${id}/reactivate`),
  getActivityLog: (limit = 50) => api.get('/platform/activity-log', { params: { limit } }),
};

// Subscription APIs
export const subscriptionAPI = {
  getPlans: () => api.get('/subscription/plans'),
  getCurrent: () => api.get('/subscription/current'),
  subscribe: (data) => api.post('/subscription/subscribe', data),
  cancel: (data) => api.post('/subscription/cancel', data),
  getHistory: () => api.get('/subscription/history'),
  getInvoice: (paymentId) => api.get(`/subscription/invoice/${paymentId}`),
  downloadInvoicePDF: (paymentId) => api.get(`/subscription/invoice/${paymentId}/pdf`, { responseType: 'blob' }),
  // Plan change (upgrade/downgrade)
  previewChange: (planId, billingCycle = 'monthly') => 
    api.get('/subscription/preview-change', { params: { new_plan_id: planId, billing_cycle: billingCycle } }),
  changePlan: (data) => api.post('/subscription/change-plan', data),
};

// Razorpay Payment APIs
export const razorpayAPI = {
  getConfig: () => api.get('/razorpay/config'),
  createOrder: (data) => api.post('/razorpay/create-order', data),
  verifyPayment: (data) => api.post('/razorpay/verify-payment', data),
};

// Platform Analytics APIs
export const analyticsAPI = {
  getAnalytics: (startDate, endDate) => api.get('/platform/analytics', { 
    params: { start_date: startDate, end_date: endDate } 
  }),
  exportCSV: (startDate, endDate) => api.get('/platform/analytics/export', { 
    params: { start_date: startDate, end_date: endDate },
    responseType: 'blob'
  }),
};

// Email & Notifications APIs
export const emailAPI = {
  getLogs: (params) => api.get('/emails/logs', { params }),
  getTemplates: () => api.get('/emails/templates'),
  testUsageAlert: () => api.post('/emails/test-usage-alert'),
  checkSubscriptionReminders: () => api.post('/emails/check-subscription-reminders'),
};

// SMS APIs
export const smsAPI = {
  send: (data) => api.post('/sms/send', data),
  sendOTP: (phone_number) => api.post('/sms/send-otp', { phone_number }),
  verifyOTP: (phone_number, code) => api.post('/sms/verify-otp', { phone_number, code }),
  getLogs: (params) => api.get('/sms/logs', { params }),
  getTemplates: () => api.get('/sms/templates'),
};

// Notification Config API
export const notificationConfigAPI = {
  getConfig: () => api.get('/notifications/config'),
};

// Notification Preferences API
export const notificationPreferencesAPI = {
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
};


// User APIs
export const userAPI = {
  getAll: () => api.get('/users'),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Patient APIs
export const patientAPI = {
  create: (data) => api.post('/patients', data),
  getAll: (params) => api.get('/patients', { params }),
  getById: (id) => api.get(`/patients/${id}`),
  update: (id, data) => api.put(`/patients/${id}`, data),
  getTimeline: (id) => api.get(`/patients/${id}/timeline`),
  assign: (id, data) => api.put(`/patients/${id}/assign`, data),
  delete: (id, reason) => api.delete(`/patients/${id}`, { params: { deletion_reason: reason } }),
  requestDeleteAuth: (id) => api.post(`/patients/${id}/delete-authorization`),
  getMyPatients: () => api.get('/dashboard/my-patients'),
  addNote: (id, data) => api.post(`/patients/${id}/note`, data),
  refer: (id, data) => api.post(`/patients/${id}/refer`, data),
  uploadDocument: (id, data) => api.post(`/patients/${id}/upload-document`, data),
  getDocuments: (id) => api.get(`/patients/${id}/documents`),
  getDeletedLog: () => api.get('/patients/deleted/log'),
  searchById: (identifier) => api.get(`/patients/search/${identifier}`),
  reopenCase: (id, data) => api.post(`/patients/${id}/reopen-case`, data),
};

// Admission APIs
export const admissionAPI = {
  create: (data) => api.post('/admissions', data),
  getAll: (params) => api.get('/admissions', { params }),
  discharge: (id) => api.put(`/admissions/${id}/discharge`),
};

// Prescription APIs
export const prescriptionAPI = {
  create: (data) => api.post('/prescriptions', data),
  getAll: (params) => api.get('/prescriptions', { params }),
  getById: (id) => api.get(`/prescriptions/${id}`),
};

// Lab Test APIs
export const labTestAPI = {
  create: (data) => api.post('/lab-tests', data),
  getAll: (params) => api.get('/lab-tests', { params }),
  update: (id, data) => api.put(`/lab-tests/${id}`, data),
};

// Imaging Test APIs
export const imagingTestAPI = {
  create: (data) => api.post('/imaging-tests', data),
  getAll: (params) => api.get('/imaging-tests', { params }),
  update: (id, data) => api.put(`/imaging-tests/${id}`, data),
};

// Stock APIs
export const stockAPI = {
  create: (data) => api.post('/stock', data),
  getAll: (params) => api.get('/stock', { params }),
  update: (id, data) => api.put(`/stock/${id}`, data),
};

// Dispense APIs
export const dispenseAPI = {
  create: (data) => api.post('/dispense', data),
  getAll: (params) => api.get('/dispenses', { params }),
};

// Procedure APIs
export const procedureAPI = {
  create: (data) => api.post('/procedures', data),
  getAll: (params) => api.get('/procedures', { params }),
  update: (id, data) => api.put(`/procedures/${id}`, data),
};

// Billing APIs
export const billingAPI = {
  create: (data) => api.post('/bills', data),
  getAll: (params) => api.get('/bills', { params }),
  getById: (id) => api.get(`/bills/${id}`),
  makePayment: (id, data) => api.post(`/bills/${id}/payment`, data),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Doctor APIs
export const doctorAPI = {
  getAll: () => api.get('/doctors'),
  getBySpecialty: (specialty) => api.get('/doctors/by-specialty', { params: { specialty } }),
};

// Nurse APIs
export const nurseAPI = {
  getAll: () => api.get('/nurses'),
};

// Appointment APIs
export const appointmentAPI = {
  create: (data) => api.post('/appointments', data),
  getAll: (params) => api.get('/appointments', { params }),
  getToday: () => api.get('/appointments/today'),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  cancel: (id) => api.delete(`/appointments/${id}`),
};

// Leave APIs
export const leaveAPI = {
  create: (data) => api.post('/leaves', data),
  getAll: (params) => api.get('/leaves', { params }),
  getCalendar: (startDate, endDate) => api.get('/leaves/calendar', { params: { start_date: startDate, end_date: endDate } }),
  approve: (id) => api.put(`/leaves/${id}/approve`),
  reject: (id, data) => api.put(`/leaves/${id}/reject`, data),
};

// Staff Availability APIs
export const staffAPI = {
  getAvailability: (date) => api.get('/staff/availability', { params: { date } }),
};

// Medication Approval APIs
export const medicationApprovalAPI = {
  create: (data) => api.post('/medication-approvals', data),
  getAll: (params) => api.get('/medication-approvals', { params }),
  approve: (id) => api.put(`/medication-approvals/${id}/approve`),
  reject: (id, data) => api.put(`/medication-approvals/${id}/reject`, data),
};

// Case Message APIs
export const messageAPI = {
  send: (data) => api.post('/messages', data),
  getAll: (params) => api.get('/messages', { params }),
  getByPatient: (patientId) => api.get(`/messages/patient/${patientId}`),
  markRead: (id) => api.put(`/messages/${id}/read`),
};

// Notification APIs
export const notificationAPI = {
  getAll: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  getCount: () => api.get('/notifications/count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Workflow APIs
export const workflowAPI = {
  getPharmacyQueue: () => api.get('/workflow/pharmacy-queue'),
  getLabQueue: () => api.get('/workflow/lab-queue'),
  getImagingQueue: () => api.get('/workflow/imaging-queue'),
  getReceptionQueue: () => api.get('/workflow/reception-queue'),
  completeItem: (referralId, data) => api.put(`/workflow/complete/${referralId}`, data),
  getPatientStatus: (patientId) => api.get(`/workflow/patient-status/${patientId}`),
  closeCase: (patientId, data) => api.post(`/workflow/case-closure/${patientId}`, data),
  createDiscountAuth: (data) => api.post('/workflow/discount-authorization', data),
  validateDiscount: (data) => api.post('/workflow/validate-discount', data),
};

// Pricing Master APIs
export const pricingAPI = {
  getAll: (category) => api.get('/pricing-master', { params: { category } }),
  create: (data) => api.post('/pricing-master', data),
  update: (id, data) => api.put(`/pricing-master/${id}`, data),
  delete: (id) => api.delete(`/pricing-master/${id}`),
  getAuditLog: () => api.get('/pricing-master/audit-log'),
};

export default api;



// Invoice APIs
export const invoiceAPI = {
  getSettings: () => api.get('/invoice-settings'),
  updateSettings: (data) => api.put('/invoice-settings', data),
  generate: (data) => api.post('/invoices/generate', data),
  getById: (id) => api.get(`/invoices/${id}`),
  getByBill: (billId) => api.get(`/bills/${billId}/invoice`),
};
